import { ResourceListPage } from "@/components/page-templates";
import { RequireScope } from "@/components/require-scope";
import { cn } from "@/lib/utils";
import { McpTabs } from "@/pages/mcp/McpTabs";
import { TableRowContextMenu } from "@/components/table-row-context-menu";
import type { Action } from "@/components/ui/MoreActions";
import { useRBAC } from "@/hooks/useRBAC";
import { useRoutes } from "@/routes";
import { useListDeploymentsSuspense } from "@gram/client/react-query/listDeployments.js";
import {
  mutationKeyRedeployDeployment,
  type RedeployDeploymentMutationVariables,
} from "@gram/client/react-query/redeployDeployment.js";
import { useMutationState } from "@tanstack/react-query";
import { Alert } from "@/components/ui/Alert";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/Dropdown";
import { Icon } from "@/components/ui/Icon";
import { Table, TableProps } from "@/components/ui/Table";
import { cloneElement, Suspense, useState } from "react";
import { Outlet } from "react-router";
import { DeploymentsEmptyState } from "./DeploymentsEmptyState";
import { useActiveDeployment } from "./useActiveDeployment";
import { useRedeployDeployment } from "./useRedeployDeployment";

export default function DeploymentsPage(): JSX.Element {
  return (
    <ResourceListPage
      scope={["project:read", "project:write"]}
      title="Deployments"
      description="Each push deploys every source in the project together, so a deployment is the version its sources and tools arrive in."
      belowHeader={<McpTabs active="deployments" />}
    >
      <Suspense fallback={<div>Loading...</div>}>
        <DeploymentsTable />
      </Suspense>
    </ResourceListPage>
  );
}

export function DeploymentsRoot(): JSX.Element {
  return <Outlet />;
}

type DeploymentSummary = {
  id: string;
  status: string;
  createdAt: Date;
  openapiv3AssetCount: number;
  openapiv3ToolCount: number;
  functionsAssetCount: number;
  functionsToolCount: number;
  externalMcpAssetCount: number;
  externalMcpToolCount: number;
};

function useDeploymentActions(
  deployment: DeploymentSummary,
  latest: boolean,
  { onSettled }: { onSettled?: () => void } = {},
): Action[] {
  const redeployMutation = useRedeployDeployment({
    onSettled() {
      onSettled?.();
    },
  });

  // A redeploy can be started from either the kebab dropdown or the row's
  // context menu, which are separate hook instances. Observe pending
  // redeploys for this deployment across all instances via the shared
  // mutation key so both menus show the pending label and disable together.
  const pendingDeploymentIds = useMutationState({
    filters: {
      mutationKey: mutationKeyRedeployDeployment(),
      status: "pending",
    },
    select: (mutation) =>
      (
        mutation.state.variables as
          | RedeployDeploymentMutationVariables
          | undefined
      )?.request.redeployRequestBody.deploymentId,
  });
  const isRedeploying =
    redeployMutation.isPending || pendingDeploymentIds.includes(deployment.id);

  // Find the current deployment to check its status
  const isCompletedDeployment = deployment.status === "completed";

  // Show actions for:
  // 1. Latest deployment (regardless of status) - shows "Retry"
  // 2. Completed deployments that are not the latest - shows "Redeploy"
  if (!latest && !isCompletedDeployment) {
    return [];
  }

  const handleRedeploy = () => {
    redeployMutation.mutate({
      request: {
        redeployRequestBody: {
          deploymentId: deployment.id,
        },
      },
    });
  };

  const actionText = latest ? "Retry Deployment" : "Rollback";
  const buttonText = isRedeploying
    ? latest
      ? "Retrying Deployment..."
      : "Rolling Back..."
    : actionText;

  return [
    {
      icon: "refresh-cw",
      label: buttonText,
      disabled: isRedeploying,
      onClick: handleRedeploy,
    },
  ];
}

function DeploymentActionsDropdown({
  deployment,
  latest,
}: {
  deployment: DeploymentSummary;
  latest: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const actions = useDeploymentActions(deployment, latest, {
    onSettled() {
      setIsOpen(false);
    },
  });

  if (actions.length === 0) {
    return null;
  }

  return (
    <RequireScope scope="project:write" level="section">
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="tertiary" size="sm" className="h-8 w-8 p-0">
            <Button.LeftIcon>
              <Icon name="ellipsis" className="size-4" />
            </Button.LeftIcon>
            <Button.Text className="sr-only">Open menu</Button.Text>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {actions.map((action, index) => (
            <DropdownMenuItem
              key={index}
              onClick={action.onClick}
              disabled={action.disabled}
              className="cursor-pointer"
            >
              {action.icon && (
                <Icon name={action.icon} className="mr-2 size-4" />
              )}
              {action.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </RequireScope>
  );
}

function DeploymentRowContextMenu({
  deployment,
  latest,
  children,
}: {
  deployment: DeploymentSummary;
  latest: boolean;
  children: React.ReactElement;
}) {
  const actions = useDeploymentActions(deployment, latest);

  // Same gate as the kebab's RequireScope, checked via RBAC directly:
  // RequireScope's component-level wrapper would insert divs around the
  // `<tr>` (invalid table markup) and gray out rows for read-only users,
  // so the row renders unwrapped with an empty menu instead.
  const { hasAnyScope } = useRBAC();
  const canWrite = hasAnyScope(["project:write"]);

  return (
    <TableRowContextMenu actions={canWrite ? actions : []}>
      {children}
    </TableRowContextMenu>
  );
}

function DeploymentsTable() {
  const { data: res } = useListDeploymentsSuspense();
  const deployments = res.items ?? [];

  const { data: activeDeployment } = useActiveDeployment();

  if (deployments.length === 0) {
    return <DeploymentsEmptyState />;
  }

  const columnsWithData: TableProps<DeploymentSummary>["columns"] = [
    {
      key: "id",
      header: "ID",
      width: "220px",
      render: (row) => (
        <div className="flex items-center gap-2">
          <DeploymentLink id={row.id} />
          {activeDeployment === row && (
            <Badge variant="success" className="px-1.5 py-0.25">
              Active
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "120px",
      render: (row) => {
        // Mono status word: muted green for success, red for failed,
        // neutral ink for everything in flight.
        const tone =
          row.status === "completed"
            ? "text-default-success"
            : row.status === "failed"
              ? "text-default-destructive"
              : "text-muted-foreground";
        const word = row.status === "completed" ? "ok" : row.status;
        return (
          <span
            className={`font-mono text-[11px] tracking-wide uppercase ${tone}`}
          >
            {word}
          </span>
        );
      },
    },
    {
      key: "createdAt",
      header: "Deployed",
      width: "150px",
      render: (row) => (
        <span className="text-muted-foreground text-sm">
          {relativeTime(row.createdAt)}
        </span>
      ),
    },
    {
      key: "assetCount",
      header: "Assets",
      render: (row) =>
        row.openapiv3AssetCount +
        row.functionsAssetCount +
        row.externalMcpAssetCount,
      width: "150px",
    },
    {
      key: "toolCount",
      header: "Tools",
      render: (row) =>
        row.openapiv3ToolCount +
        row.functionsToolCount +
        row.externalMcpToolCount,
      width: "120px",
    },
    {
      key: "actions",
      header: "",
      render: (row) => {
        return (
          <div className="flex w-full justify-end">
            <DeploymentActionsDropdown
              deployment={row}
              latest={deployments[0] === row}
            />
          </div>
        );
      },
      width: "1fr",
    },
  ];

  return (
    <>
      <DeploymentsExplainer />

      <Table<DeploymentSummary>
        columns={columnsWithData}
        rowKey={(row) => row.id}
        data={deployments}
        className="mb-8 overflow-auto"
        renderRow={(row, rowElement) => (
          <DeploymentRowContextMenu
            key={row.id}
            deployment={row}
            latest={deployments[0] === row}
          >
            {/* Only one deployment is serving; the others are history, and
                read that way. The class goes on the row itself: a wrapper
                would be a div inside <tbody>, and `display: contents`
                generates no box for the opacity to apply to. */}
            {activeDeployment === row ? rowElement : dimmed(rowElement)}
          </DeploymentRowContextMenu>
        )}
      />
    </>
  );
}

/** The row, marked as history rather than the deployment in force. */
function dimmed(rowElement: React.ReactElement): React.ReactElement {
  const row = rowElement as React.ReactElement<{ className?: string }>;
  return cloneElement(row, {
    className: cn(row.props.className, "opacity-70"),
  });
}

function DeploymentsExplainer() {
  return (
    <Alert variant="info" dismissible={false} className="mb-6 text-sm">
      Adding or updating a source creates a deployment: every source in the
      project is analyzed, and the tool definitions they produce are generated
      or updated together.
    </Alert>
  );
}

function DeploymentLink({ id }: { id: string }) {
  const routes = useRoutes();
  return (
    <routes.deployments.deployment.Link params={[id]}>
      <span className="font-mono" title={id}>
        {id.slice(0, 8)}
      </span>
    </routes.deployments.deployment.Link>
  );
}

function relativeTime(date: Date): string {
  const now = Date.now();
  const createdAt = date.getTime();
  const diffMs = now - createdAt;

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

  if (days > 0) {
    return rtf.format(-days, "day");
  } else if (hours > 0) {
    return rtf.format(-hours, "hour");
  } else if (minutes > 0) {
    return rtf.format(-minutes, "minute");
  } else {
    return rtf.format(-seconds, "second");
  }
}
